import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, ChevronDown, ChevronUp, ExternalLink, Ruler, AlertCircle } from 'lucide-react';

// Correctif icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/**
 * MapReadyHandler : force invalidateSize() dès que la carte est prête, puis à chaque
 * changement de taille du conteneur parent (résout le bug "tuiles grises après zoom"
 * dans les collapsibles). Ajoute aussi les outils de mesure Leaflet-Draw.
 */
function MapReadyHandler() {
    const map = useMap();
    useEffect(() => {
        if (!map) return;

        // 1) Force le recalcul de la taille immédiatement + à intervalles courts
        //    (couvre les cas où le parent finit d'ouvrir son animation).
        const kicks = [50, 200, 400, 800];
        const timers = kicks.map((ms) => setTimeout(() => {
            try { map.invalidateSize({ animate: false, pan: false }); } catch (_) { /* ignore */ }
        }, ms));

        // 2) Surveille le container parent : à chaque redimensionnement, on invalide.
        const container = map.getContainer();
        const ro = new ResizeObserver(() => {
            try { map.invalidateSize({ animate: false, pan: false }); } catch (_) { /* ignore */ }
        });
        if (container?.parentElement) ro.observe(container.parentElement);

        // 3) Ajoute les outils de mesure (draw)
        const drawnItems = new L.FeatureGroup();
        map.addLayer(drawnItems);
        const drawControl = new L.Control.Draw({
            position: 'topright',
            edit: { featureGroup: drawnItems, remove: true },
            draw: {
                polygon: { allowIntersection: false, showArea: true, metric: true,
                          shapeOptions: { color: '#f59e0b', weight: 3 } },
                polyline: { metric: true, shapeOptions: { color: '#3b82f6', weight: 3 } },
                rectangle: false, circle: false, circlemarker: false, marker: false,
            },
        });
        map.addControl(drawControl);
        const onCreated = (e) => {
            drawnItems.addLayer(e.layer);
            if (e.layerType === 'polygon') {
                const pts = e.layer.getLatLngs()[0];
                const area = L.GeometryUtil?.geodesicArea?.(pts) ?? 0;
                const txt = area > 10000 ? `${(area / 10000).toFixed(2)} ha` : `${Math.round(area)} m²`;
                e.layer.bindPopup(`Surface : ${txt}`).openPopup();
            } else if (e.layerType === 'polyline') {
                const pts = e.layer.getLatLngs();
                let d = 0;
                for (let i = 1; i < pts.length; i++) d += pts[i - 1].distanceTo(pts[i]);
                const txt = d > 1000 ? `${(d / 1000).toFixed(2)} km` : `${Math.round(d)} m`;
                e.layer.bindPopup(`Distance : ${txt}`).openPopup();
            }
        };
        map.on(L.Draw.Event.CREATED, onCreated);

        return () => {
            timers.forEach(clearTimeout);
            ro.disconnect();
            map.off(L.Draw.Event.CREATED, onCreated);
            try { map.removeControl(drawControl); } catch (_) { /* ignore */ }
            try { if (map.hasLayer(drawnItems)) map.removeLayer(drawnItems); } catch (_) { /* ignore */ }
        };
    }, [map]);
    return null;
}

/**
 * Module Géoportail — vue satellite du site du dossier.
 * - Géocodage BAN une seule fois puis mis en cache tant que le prospect ne change pas.
 * - Carte fluide : invalidateSize() + ResizeObserver pour éviter les tuiles grises au zoom.
 * - Tuiles WMTS IGN (photo aérienne par défaut) avec fallback OSM.
 * - Outils de mesure intégrés + bouton d'ouverture externe.
 */
export function GeoportailModule({ prospect }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [coords, setCoords] = useState(null);
    const [matchedLabel, setMatchedLabel] = useState('');
    const [error, setError] = useState(null);
    const geocodedForRef = useRef(null); // Adresse déjà géocodée (évite re-fetch)

    const fullAddress = useMemo(() => {
        const ep = prospect?.easy_power_data || {};
        // Priorité : adresse principale du dossier (=celle du contact / fiche prospect).
        // Fallback : adresse spécifique du contrat Courtage si l'adresse principale
        // est vide. Ainsi une modification de l'adresse dossier est IMMÉDIATEMENT
        // reflétée sur la carte Geoportail (invalidation du cache géocodage automatique).
        return [
            prospect?.address || ep.adresse_contrat || '',
            prospect?.postal_code || ep.code_postal || '',
            prospect?.city || ep.ville || '',
        ].filter(Boolean).join(' ').trim();
    }, [prospect]);

    // Réinitialise le cache si l'adresse change
    useEffect(() => {
        if (geocodedForRef.current && geocodedForRef.current !== fullAddress) {
            geocodedForRef.current = null;
            setCoords(null);
            setMatchedLabel('');
        }
    }, [fullAddress]);

    // Géocode LA PREMIÈRE FOIS que l'utilisateur ouvre le module
    useEffect(() => {
        if (!open) return;
        if (coords) return;
        if (geocodedForRef.current === fullAddress) return;
        if (!fullAddress) {
            setError("Aucune adresse renseignée sur ce dossier.");
            return;
        }
        setLoading(true);
        setError(null);
        const controller = new AbortController();
        fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}&limit=1`,
              { signal: controller.signal })
            .then((r) => r.json())
            .then((data) => {
                const feat = data?.features?.[0];
                if (!feat) {
                    setError("Adresse introuvable sur la Base Adresse Nationale. Corrigez l'adresse du dossier.");
                    return;
                }
                const [lng, lat] = feat.geometry.coordinates;
                setCoords([lat, lng]);
                setMatchedLabel(feat.properties?.label || fullAddress);
                geocodedForRef.current = fullAddress;
            })
            .catch((e) => {
                if (e.name !== 'AbortError') setError(`Erreur de géocodage : ${e.message}`);
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [open, fullAddress, coords]);

    const externalUrl = useMemo(() => {
        if (!coords) return 'https://www.geoportail.gouv.fr/carte';
        return `https://www.geoportail.gouv.fr/carte?c=${coords[1]},${coords[0]}&z=18&l0=ORTHOIMAGERY.ORTHOPHOTOS::GEOPORTAIL:OGC:WMTS(1)&permalink=yes`;
    }, [coords]);

    return (
        <Card data-testid="geoportail-module">
            <CardHeader className="cursor-pointer select-none" onClick={() => setOpen((v) => !v)}>
                <CardTitle className="flex items-center justify-between text-base">
                    <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-emerald-600" />
                        <span>Géoportail — Vue satellite du site</span>
                        {fullAddress && !open && (
                            <span className="text-xs text-slate-500 font-normal ml-2 hidden sm:inline">
                                {fullAddress}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {!fullAddress && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertCircle className="w-3.5 h-3.5" />
                                Adresse manquante
                            </span>
                        )}
                        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                </CardTitle>
            </CardHeader>
            {open && (
                <CardContent className="space-y-3" data-testid="geoportail-content">
                    {loading && (
                        <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Localisation en cours…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>{error}</div>
                        </div>
                    )}
                    {coords && !error && (
                        <>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="text-xs text-slate-600 flex items-center gap-1.5">
                                    <MapPin className="w-3.5 h-3.5" />
                                    <span data-testid="geoportail-matched-label">{matchedLabel}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500 hidden md:inline-flex items-center gap-1">
                                        <Ruler className="w-3.5 h-3.5" /> Outils en haut à droite
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.open(externalUrl, '_blank', 'noopener,noreferrer')}
                                        data-testid="geoportail-external-btn"
                                        className="h-7 text-xs"
                                    >
                                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                                        Ouvrir Géoportail (thermographie, groupes froids…)
                                    </Button>
                                </div>
                            </div>
                            {/* IMPORTANT : `key` forcée sur coords → si l'adresse change, on
                                remonte proprement la MapContainer (react-leaflet ne réinit
                                pas le center après création). */}
                            <div className="rounded-lg overflow-hidden border" style={{ height: '520px' }} data-testid="geoportail-map">
                                <MapContainer
                                    key={`${coords[0]}-${coords[1]}`}
                                    center={coords}
                                    zoom={18}
                                    style={{ height: '100%', width: '100%' }}
                                    scrollWheelZoom
                                    preferCanvas
                                    zoomAnimation
                                >
                                    <LayersControl position="topright">
                                        <LayersControl.BaseLayer checked name="Photo aérienne (IGN)">
                                            <TileLayer
                                                attribution='© IGN Géoportail'
                                                url="https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&FORMAT=image/jpeg&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}"
                                                maxNativeZoom={19}
                                                maxZoom={21}
                                                updateWhenIdle={false}
                                                updateWhenZooming={false}
                                                keepBuffer={4}
                                                crossOrigin
                                            />
                                        </LayersControl.BaseLayer>
                                        <LayersControl.BaseLayer name="Plan IGN">
                                            <TileLayer
                                                attribution='© IGN Géoportail'
                                                url="https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&FORMAT=image/png&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}"
                                                maxNativeZoom={18}
                                                maxZoom={19}
                                                updateWhenIdle={false}
                                                updateWhenZooming={false}
                                                keepBuffer={4}
                                                crossOrigin
                                            />
                                        </LayersControl.BaseLayer>
                                        <LayersControl.BaseLayer name="OpenStreetMap">
                                            <TileLayer
                                                attribution='&copy; OpenStreetMap'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                maxZoom={19}
                                                updateWhenIdle={false}
                                                keepBuffer={4}
                                            />
                                        </LayersControl.BaseLayer>
                                        <LayersControl.Overlay name="Cadastre">
                                            <TileLayer
                                                attribution='© IGN'
                                                url="https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=CADASTRALPARCELS.PARCELLAIRE_EXPRESS&STYLE=normal&FORMAT=image/png&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}"
                                                opacity={0.75}
                                                maxNativeZoom={19}
                                                maxZoom={21}
                                                updateWhenIdle={false}
                                                keepBuffer={4}
                                            />
                                        </LayersControl.Overlay>
                                    </LayersControl>
                                    <Marker position={coords}>
                                        <Popup>
                                            <strong>{prospect?.company_name || 'Site'}</strong>
                                            <br />
                                            <span className="text-xs">{matchedLabel}</span>
                                        </Popup>
                                    </Marker>
                                    <MapReadyHandler />
                                </MapContainer>
                            </div>
                            <p className="text-[11px] text-slate-500 italic">
                                Astuce : utilisez les icônes en haut à droite pour tracer une distance ou une surface.
                                Pour la thermographie et les groupes froids, ouvrez Géoportail dans un nouvel onglet.
                            </p>
                        </>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

export default GeoportailModule;
